import { CSSProperties, useContext, useEffect, useState } from "react"
import { Alert } from "@mui/material"
import { pageView } from "analytics"
import { UpgradeModalContext } from "Hooks/UpgradeModalContext"
import useOrganization from 'Hooks/useOrganization'
import lowerCase from "lodash/lowerCase"
import startCase from "lodash/startCase"
import { useLocation } from "react-router-dom"
import { getStatusToShowPaymentFailedBanner } from "modules/Utils"
import AccountTab from "./Components/AccountTab/AccountTab"
import AdvancedSettingsTab from "./Components/AdvancedSettingsTab"
import BillingTab from "./Components/BillingTab"
import UsersTab from "./Components/UsersTab/UsersTab"
import OrganizationSubscriptionStatusBanner from "components/shareable/OrganizationSubscriptionStatusBanner"
import ButtonSymphony from "elements/ButtonSymphony"
import Colors from "modules/Colors"
import PageSymphony from "elements/PageSymphony"
import useCurrentBrand, { BrandExistsState } from "Hooks/useCurrentBrand"
import DebugBox from "components/shareable/DebugBox"
import CardSymphony from "elements/CardSymphony"
import TabsSymphony, { TabItem } from "elements/TabsSymphony"
import useScreen from "Hooks/useScreen"
import TabSymphony from "elements/TabSymphony"
import Sty from "modules/Sty"
import PrivacyAndTerms from "components/shareable/PrivacyAndTerms"
import Icon from "elements/Icon"

type GlobalWindow = Window & {
    Intercom: (type: string) => void
}

enum PageTabs {
    ACCOUNT = "ACCOUNT",
    USERS = "USERS",
    BILLING = "BILLING",
    // ADVANCED_SETTINGS = "ADVANCED_SETTINGS",
    PRIVACY_AND_TERMS = "PRIVACY_AND_TERMS",
}

enum PageTabsViewOnly {
    ACCOUNT = "ACCOUNT",
}

// Create new organization tabs array
const ORGANIZATION_TABS: TabItem[] = [
    PageTabs.ACCOUNT,
].map((tab) => ({
    id: tab,
    title: startCase(lowerCase(tab)),
}))

const SETTINGS_TABS: TabItem[] = [
    PageTabs.USERS,
    PageTabs.ACCOUNT,
    PageTabs.BILLING,
    PageTabs.PRIVACY_AND_TERMS,
].map((tab) => ({
    id: tab,
    title: startCase(lowerCase(tab)),
}))

const viewOnlyTabs = [
    PageTabsViewOnly.ACCOUNT,
].map((tab) => ({
    value: tab,
    name: startCase(lowerCase(tab)),
}))

const SettingsPage = () => {
    const {
        mobileView,
    } = useScreen()
    const { currentBrand, isBrandAdmin } = useCurrentBrand() as BrandExistsState
    const { handleOpenModal } = useContext(UpgradeModalContext);
    const {
        organization,
        brandIdIsInOrganization,
        remainingFreeTrial,
        isOrganizationInactive
    } = useOrganization()

    const showPaymentFailedBanner = getStatusToShowPaymentFailedBanner(currentBrand)
    let defaultTab: TabItem = SETTINGS_TABS[0];

    // used to check if the brand belongs to the organization
    // so we can render either organization-specific View, or regular pro/free view
    const inOrganization = brandIdIsInOrganization(currentBrand.id)

    if (organization && inOrganization) {
        defaultTab = SETTINGS_TABS[1];
    } else if (isBrandAdmin) {
        defaultTab = SETTINGS_TABS[0];
    }

    const [selectedSettingsTab, setSelectedSettingsTab] = useState<TabItem>(defaultTab);
    const handleClickSupportButton = () => {
        const globalWindow = window as unknown as GlobalWindow
        if (globalWindow.Intercom) {
            globalWindow.Intercom("show")
        }
    }

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const tabFromQuery = queryParams.get("tab") as PageTabs;

    useEffect(() => {
        if (tabFromQuery && Object.values(PageTabs).includes(tabFromQuery)) {
            setSelectedSettingsTab(SETTINGS_TABS.find((tab) => tab.id === tabFromQuery) as TabItem);
        }
    }, [tabFromQuery]);

    useEffect(() => {
        pageView("Settings");
    }, []);

    const getTabsToRender = (
        brandBelongsToOrganzation: boolean,
    ) => {
        if (brandBelongsToOrganzation) {
            return ORGANIZATION_TABS;
        }

        if (isBrandAdmin) {
            return SETTINGS_TABS;
        }

        return viewOnlyTabs;
    };

    const renderAccountTab = () => {
        return (
            <TabSymphony className="settingsPageTabAccount" visible={selectedSettingsTab.id === PageTabs.ACCOUNT}>
                <AccountTab />
            </TabSymphony>
        )
    }
    const renderUsersTab = () => {
        return (
            <TabSymphony className="settingsPageTabUsers" visible={selectedSettingsTab.id === PageTabs.USERS}>
                {organization?.status !== "ready" &&
                    <Alert
                        className="w-full"
                        icon={<Icon className="settingsPageUpgradeToProIcon" icon="audiences" />}
                        sx={{
                            background: `${Colors.purple}14`,
                            borderRadius: "12px",
                            marginBottom: "32px",
                            "& .MuiAlert-message": {
                                display: "flex",
                                width: "100%",
                                justifyContent: "space-between",
                                alignItems: "center"
                            }
                        }}
                    >
                        <div className="flex flex-col">
                            <div style={Sty.text20purple}>
                                Unlock discounted pricing with Symphony for Teams
                            </div>
                            <div style={Sty.text16grey}>
                                Save money + time with a Team Account
                            </div>
                        </div>
                        <div>
                            <ButtonSymphony
                                className="settingsPageUpgradeToProButton"
                                variant="outlined"
                                onClick={() => handleOpenModal({
                                    source: "Settings Page - Upgrade to Pro Button",
                                    tab: "team"
                                })}
                            >
                                Upgrade
                            </ButtonSymphony>
                        </div>
                    </Alert>
                }
                <UsersTab
                    organization={organization}
                    brandBelongsToOrganization={inOrganization}
                />
            </TabSymphony>
        )
    }

    const renderBillingTab = () => {
        return (
            <TabSymphony className="settingsPageTabBilling" visible={selectedSettingsTab.id === PageTabs.BILLING}>
                <BillingTab
                    brandBelongsToOrganization={inOrganization}
                    organization={organization}
                />
            </TabSymphony>
        )
    }

    const renderPrivacyPolicyTab = () => {
        return (
            <TabSymphony className="settingsPageTabPrivacyPolicy" visible={selectedSettingsTab.id === PageTabs.PRIVACY_AND_TERMS}>
                <PrivacyAndTerms />
            </TabSymphony>
        )
    }

    return (
        <PageSymphony className="settingsPageComponent" isContent>
            {(showPaymentFailedBanner || remainingFreeTrial() > 0 || isOrganizationInactive()) &&
                <OrganizationSubscriptionStatusBanner />
            }
            <CardSymphony
                className="settingsPageCard"
                title="Settings"
                subtitle="Manage your account settings and preferences."
            >
                <TabsSymphony
                    className="settingsPageTabs"
                    selectedTab={selectedSettingsTab}
                    onChange={(tab) => setSelectedSettingsTab(tab as TabItem)}
                    tabs={SETTINGS_TABS}
                    isBorderBottom
                    rightSideContent={!mobileView &&
                        <ButtonSymphony
                            className="settingsPageSupportButton"
                            onClick={handleClickSupportButton}
                        >
                            Support / Help
                        </ButtonSymphony>
                    }
                />
                {renderAccountTab()}
                {renderUsersTab()}
                {renderBillingTab()}
                {renderPrivacyPolicyTab()}
            </CardSymphony>

        </PageSymphony>
    )
}

const sty: Record<string, CSSProperties> = {}

export default SettingsPage
